import { useState, useCallback, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../providers/AuthProvider";
import useSecureDocumentUploadsApiService from "../services/api/SecureDocumentUploadsApiService";
import { SecureDocumentUploadsResponse } from "../services/request/SecureDocumentUploadsRequest";
import { sendZapierWebhook, createWebhookData } from "../utils/webhookUtils";

export interface FileRejection {
  file: File;
  errors: { message: string; code: string }[];
}

interface UseSecureFileUploadReturn {
  file: File | undefined;
  uploadProgress: number;
  isUploading: boolean;
  isRequestingSignedUrl: boolean;
  uploadData: SecureDocumentUploadsResponse | undefined;
  errorMessage: string | null;
  handleFileSelect: (files: File[]) => void;
  handleFileReject: (fileRejections: FileRejection[]) => void;
  resetUpload: () => void;
}

export const useSecureFileUpload = (): UseSecureFileUploadReturn => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [webhookSent, setWebhookSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const authProfile = useContext(AuthContext);

  const {
    mutate: requestSignedUrl,
    isLoading: isRequestingSignedUrl,
    data: uploadData,
  } = useSecureDocumentUploadsApiService();

  const isUploading = uploadProgress > 0 && uploadProgress < 100;

  const getSignedUrl = useCallback(() => {
    if (!file) {
      return;
    }

    const fileName = file.name;
    const fileExtension = fileName.split(".").pop() || "";

    requestSignedUrl({
      file_name: fileName,
      file_extension: fileExtension,
      upload_type: "document",
    });
  }, [file]);

  const uploadFile = useCallback(async () => {
    if (!uploadData || !file || webhookSent) {
      return;
    }

    try {
      await axios.put(uploadData.signed_url, file, {
        headers: {
          "Content-Type": uploadData.content_type,
          "X-Goog-Content-Length-Range": `${uploadData.min_size},${uploadData.max_size}`,
        },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          const percentCompleted =
            total > 0 ? Math.round((loaded * 100) / total) : 0;
          setUploadProgress(percentCompleted);
        },
      });

      setUploadProgress(100);
      setWebhookSent(true);

      // Send webhook notification
      if (uploadData.file_path && authProfile) {
        const webhookData = createWebhookData(
          uploadData.file_path,
          authProfile
        );
        await sendZapierWebhook(webhookData);
      }
    } catch (error) {
      setUploadProgress(0);

      // Determine user-friendly error message based on error type
      let userMessage = "Something went wrong while preparing your upload.";

      if (axios.isAxiosError(error)) {
        if (
          error.code === "ERR_NETWORK" ||
          error.message.includes("Network Error")
        ) {
          userMessage = "Your connection was interrupted while uploading.";
        } else if (error.response?.status === 413) {
          userMessage = "Your file is too large to upload.";
        } else if (error.response?.status && error.response.status >= 500) {
          userMessage = "We couldn't finish processing your upload.";
        }
      }

      setErrorMessage(userMessage);
    }
  }, [uploadData, file, authProfile, webhookSent]);

  const handleFileReject = (fileRejections: FileRejection[]): void => {
    fileRejections.forEach((fileRejection) => {
      fileRejection.errors.forEach((fileError) => {
        if (fileError.code === "file-too-large") {
          setErrorMessage("Your file is too large to upload.");
        } else if (fileError.code === "file-invalid-type") {
          setErrorMessage("Your file type wasn't supported.");
        } else {
          setErrorMessage("Something went wrong while preparing your upload.");
        }
      });
    });
  };

  const handleFileSelect = (files: File[]): void => {
    setFile(files[0]);
  };

  const resetUpload = (): void => {
    setFile(undefined);
    setUploadProgress(0);
    setWebhookSent(false);
    setErrorMessage(null);
  };

  useEffect(() => {
    if (file) {
      getSignedUrl();
    }
  }, [file]);

  useEffect(() => {
    if (uploadData && !webhookSent) {
      uploadFile();
    }
  }, [uploadData, webhookSent]);

  return {
    file,
    uploadProgress,
    isUploading,
    isRequestingSignedUrl,
    uploadData,
    errorMessage,
    handleFileSelect,
    handleFileReject,
    resetUpload,
  };
};
